import React, { useEffect, useState, useMemo } from 'react';
import { Form, Select } from 'antd';
import { useDispatch } from 'react-redux';
import { setSearchLiveRanges } from '../../../pace/pace-actions';
import { api } from '@med-fe/util';
import { SuffixIcon } from '@med-fe/ui';

interface Employee {
  employeeID: string;
  employeeName: string;
}

function SearchTemplateViewer({ search }: any) {
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch employees list on component mount
    const fetchEmployees = async () => {
      setLoading(true);
      try {
        const data = await api.get('/odexception/EmpListHavingActiveExceptions', true, false);
        console.log(data);
        // Remove duplicates based on employeeID
        const uniqueEmployees = data?.reduce((acc: Employee[], current: Employee) => {
          const isDuplicate = acc.find((emp) => emp.employeeID === current.employeeID);
          if (!isDuplicate) {
            acc.push(current);
          }
          return acc;
        }, []);
        setEmployees(uniqueEmployees || []);
      } catch (error) {
        console.error('Error fetching employees:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchEmployees();
  }, []);

  // Update form value when search.employeeId changes (e.g., when coming back from details)
  useEffect(() => {
    if (search?.employeeId) {
      form.setFieldsValue({ employeeId: search.employeeId });
    } else {
      form.setFieldsValue({ employeeId: undefined });
    }
  }, [search?.employeeId, form]);

  const handleEmployeeChange = (value: string) => {
    if (value) {
      const selectedEmployee = employees.find((emp) => emp.employeeID === value);
      dispatch(
        setSearchLiveRanges({
          ...search,
          employeeName: selectedEmployee?.employeeName,
          employeeId: value,
          page: 0,
          size: search?.size || 15,
          base: false,
          path: 'mapped-range0',
          search: true,
          regionIds: search?.regionIds || [],
          districtIds: search?.districtIds || [],
        })
      );
    } else {
      // Clear search when no employee is selected
      dispatch(
        setSearchLiveRanges({
          ...search,
          employeeName: undefined,
          employeeId: undefined,
          page: 0,
          size: search?.size || 15,
          base: false,
          path: 'mapped-range0',
          search: false,
          regionIds: search?.regionIds || [],
          districtIds: search?.districtIds || [],
        })
      );
    }
  };

  // Memoize options to prevent recreation on every render
  const employeeOptions = useMemo(() => {
    return employees.map((emp) => ({
      key: emp.employeeID,
      value: emp.employeeID,
      label: `${emp.employeeName} (${emp.employeeID})`,
    }));
  }, [employees]);

  return (
    <div className='search-container'>
      <Form form={form} layout='vertical' initialValues={{ employeeID: search?.employeeId }}>
        <Form.Item
          key='employeeSearch'
          label={'Search By Employee'}
          name='employeeID'
          className='label-text'
          style={{ marginBottom: 0 }}
        >
          <Select
            showSearch
            allowClear
            loading={loading}
            placeholder='Select an employee...'
            suffixIcon={<SuffixIcon />}
            style={{ width: 300 }}
            onChange={handleEmployeeChange}
            value={search?.employeeId}
            options={employeeOptions}
            filterOption={(inputValue, option: any) => {
              return option.label.toLowerCase().includes(inputValue.toLowerCase());
            }}
          />
        </Form.Item>
      </Form>
    </div>
  );
}

export default SearchTemplateViewer;
